import Chart from 'react-apexcharts';
import { useStore } from '@lib/state-engine';
import { $warehouse } from '@features/remove-warehouse/model';
import { $agricultureById } from '@@/agriculture/store/agriculture.store';
import { $warehouseInfo, $warehouseStatistic } from '@entities/warehouse/store';
import { Tooltip } from './tooltip';
import styles from './styles.module.sass';
import React, { useEffect } from 'react';
import { CultureInfo } from '@@/warehouses/ts';
import { formatPrice as fp } from '@@/common/utils/price-formatters.util';
import { formatPriceToReadable } from '@/modules/common/utils/format-price-to-readable';

export const Statistic = (props: IStatisticProps) => {
  const wh = useStore($warehouse);
  const agricultureById = useStore($agricultureById);

  const statistic = useStore($warehouseStatistic);
  const reserveColors = ['#6bca41', '#778af2', '#f29077'];
  const assignColor: (p: CultureInfo, i: number) => void = (p, i) =>
    (p.color =
      Object.values(agricultureById).find(
        (a) => a.name.toLowerCase() == p.name.toLowerCase(),
      )?.color || reserveColors[i]);
  statistic?.price.forEach(assignColor);
  statistic?.weight.forEach(assignColor);

  const cultures: { [key: number]: { weight: number; cost: number } } = {};
  if (wh) {
    wh.bags.forEach((b) => {
      if (!b.agricultureId) return;
      if (!(b.agricultureId in cultures))
        cultures[b.agricultureId] = { weight: 0, cost: 0 };
      cultures[b.agricultureId].weight += b.weight;
    });
  }

  // const labels = Object.keys(cultures).map((c) => agricultureById[+c].name);
  Object.values(cultures).forEach((c) => (c.cost = Math.round(Math.random() * 100)));

  const priceColors: { [key: string]: string } = {};
  priceColors[statistic?.price[0].name || '1'] = '#EBD8A8';
  priceColors[statistic?.price[1].name || '1'] = '#FFCF71';
  priceColors[statistic?.price[2].name || '1'] = '#E9ED6F';
  priceColors['другое'] = '#409BFF';

  const priceLabels = statistic?.price.filter((w) => w.price).map((w) => w.name);

  const stateCost: {
    options: ApexCharts.ApexOptions;
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  } = {
    options: {
      stroke: {
        lineCap: 'round',
      },
      legend: {
        show: true,
        floating: false,
        height: 300,
        markers: {
          width: 40,
          height: 10,
        },
        formatter(legendName: string, opts?: any): string {
          return (
            '<b>' +
            legendName +
            '</b><br>' +
            formatPriceToReadable(opts.w.globals.seriesTotals[opts.seriesIndex])
              .number +
            ' ' +
            formatPriceToReadable(opts.w.globals.seriesTotals[opts.seriesIndex]).label +
            ' ₽'
          );
        },
      },
      tooltip: {
        enabled: false,
      },
      colors: [
        statistic?.price[0].color,
        statistic?.price[1].color,
        statistic?.price[2].color,
        '#409BFF',
      ],
      dataLabels: {
        enabled: false,
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none',
            value: 0.35,
          },
        },
      },
      plotOptions: {
        pie: {
          // offsetX: 34,
          // offsetY: 34,
          // customScale: 0.9,
          donut: {
            size: '85%',
            labels: {
              show: true,
              name: {},
              value: {
                show: true,
                formatter(val: string): string {
                  return val + ' ₽';
                },
              },
              total: {
                show: true,
                label: 'всего',
                formatter(w: any): string {
                  return `
                    ${
                      formatPriceToReadable(
                        w.config.series.reduce((t: number, s: number) => t + s),
                      ).number
                    }   ${
                    formatPriceToReadable(
                      w.config.series.reduce((t: number, s: number) => t + s),
                    ).label
                  }  
                        ₽`;
                },
              },
            },
          },
        },
      },

      labels:
        priceLabels && priceLabels.length > 4
          ? [...priceLabels.slice(0, 3), 'другое']
          : priceLabels,
    },
    series: statistic?.price
      .filter((p) => p.price)
      .reduce((result: number[], pInfo) => {
        if (result.length == 4) {
          result[3] += pInfo.price || 0;
          return result;
        }
        result.push(pInfo.price || 0);
        return result;
      }, []) || [0, 0, 0, 0],
  };

  const weightLabels = statistic?.weight.filter((w) => w.weight).map((w) => w.name);
  const stateWeight: {
    options: ApexCharts.ApexOptions;
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  } = {
    options: {
      legend: {
        show: true,
        floating: false,
        markers: {
          width: 40,
          height: 10,
        },
        formatter(legendName: string, opts?: any): string {
          return (
            '<b>' +
            legendName +
            '</b><br>' +
            fp(opts.w.globals.seriesTotals[opts.seriesIndex].toFixed(2)) +
            ' т'
          );
        },
        onItemClick: {
          toggleDataSeries: false,
        },
      },

      tooltip: {
        enabled: false,
      },
      colors: [
        //@ts-ignore
        function ({ _, seriesIndex }) {
          return statistic?.weight[seriesIndex].color;
        },
      ],
      dataLabels: {
        enabled: false,
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none',
            value: 0.35,
          },
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '85%',
            labels: {
              show: true,
              name: {},
              value: {
                formatter(val: string): string {
                  return val + ' т';
                },
              },
              total: {
                show: true,
                label: 'всего',
                formatter(w: any): string {
                  return (
                    fp(
                      w.config.series
                        .reduce((t: number, s: number) => t + s)
                        .toFixed(2),
                    ) + ' т'
                  );
                },
              },
            },
          },
        },
      },
      labels: weightLabels,
    },
    series:
      statistic?.weight
        .filter((w) => w.weight)
        .reduce((result: number[], pInfo) => {
          // @ts-ignore
          result.push(Number(pInfo.weight.toFixed(2)) || 0);

          return result;
        }, []) || [],
  };

  useEffect(() => {
    document
      .querySelectorAll('.apexcharts-legend-series')
      .forEach(
        (elem) =>
          (elem.style = 'display:flex;align-items: center;padding-bottom: 0.3em'),
      );

    document
      .querySelectorAll('.apexcharts-legend-text')
      .forEach((elem) => (elem.style = 'padding-left: 25px'));
  }, []);

  return (
    <div>
      {statistic?.price.some((p) => p.price) ? (
        <div className={styles.chartBlock}>
          {wh ? (
            <>
              <h3>Стоимость по культурам</h3>
              <Chart
                id="cost_chart"
                options={stateCost.options}
                series={stateCost.series}
                type="donut"
                width="90%"
                height={250}
              />
              <Tooltip
                id="price_tooltip"
                items={statistic?.price.slice(3)}
                measure="₽"
              />
            </>
          ) : (
            'Нет данных'
          )}
        </div>
      ) : (
        <></>
      )}
      {statistic?.weight.some((w) => w.weight) ? (
        <div className={styles.chartBlock}>
          {wh ? (
            <>
              <h3>Вес по культурам</h3>
              <Chart
                id="weight_chart"
                options={stateWeight.options}
                series={stateWeight.series}
                type="donut"
                width="90%"
                height={250}
              />
            </>
          ) : (
            'Нет данных'
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

interface IStatisticProps {
  id?: number;
}
